import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, HostListener, Input, ViewChild, Output, Component, Injectable, ViewEncapsulation, NgModule } from '@angular/core';
import * as i3 from '@engineering11/ui-lib/e11-directives';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { computePosition, shift, autoUpdate } from '@floating-ui/dom';

const defaultPopoverConfig = {
    id: `popover_${Math.ceil(Math.random() * 1000000).toString()}`,
    position: 'bottom',
    targetElement: '',
    appendToId: undefined,
    mobileWidth: 640,
    margins: 32,
    hasCloser: false,
    closeOnEscape: false,
    closeOnClickOutside: false,
    closeOnSelect: false,
    repositionDynamically: true,
    darkMode: false,
    hasPadding: false,
    maxWidth: undefined,
};
class E11PopoverComponent {
    get popoverCurrentlyOpen() {
        return this._popoverCurrentlyOpen;
    }
    set popoverCurrentlyOpen(value) {
        this._popoverCurrentlyOpen = value;
        if (value !== this.id) {
            this.closePopover();
        }
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.menuActive = false;
        this.id = defaultPopoverConfig.id;
        this.position = defaultPopoverConfig.position;
        this.targetElement = defaultPopoverConfig.targetElement;
        this.appendToId = defaultPopoverConfig.appendToId;
        this.mobileWidth = defaultPopoverConfig.mobileWidth;
        this.margins = defaultPopoverConfig.margins;
        this.hasCloser = defaultPopoverConfig.hasCloser;
        this.closeOnEscape = defaultPopoverConfig.closeOnEscape;
        this.closeOnClickOutside = defaultPopoverConfig.closeOnClickOutside;
        this.closeOnSelect = defaultPopoverConfig.closeOnSelect;
        this.repositionDynamically = defaultPopoverConfig.repositionDynamically;
        this.darkMode = defaultPopoverConfig.darkMode;
        this.hasPadding = defaultPopoverConfig.hasPadding;
        this.maxWidth = defaultPopoverConfig.maxWidth;
        this.containerClassOverrides = defaultPopoverConfig.containerClassOverrides;
        this.contentClassOverrides = defaultPopoverConfig.contentClassOverrides;
        this.clickOptionEvent = new EventEmitter();
        this.popoverCurrent = new EventEmitter();
        this.popoverClosed = new EventEmitter();
        this.isBrowser = inject(IS_BROWSER);
        // Opening sideover to edit details
        this._popoverCurrentlyOpen = '';
        this.autoUpdateRef = undefined;
        this.destroy$ = new Subject();
        this.updatePosition = async () => {
            const { x, y } = await computePosition(this.target, this.popover, {
                placement: this.transformPositionForFloatingUi(this.position),
                middleware: [shift({ padding: 8 })],
            });
            const { minWidth, maxWidth } = this.computePopoverWidth();
            this.setPopoverStyle({ top: y, left: x, minWidth, maxWidth });
        };
    }
    onKeydownHandler(event) {
        if (this.closeOnEscape && this.menuActive) {
            this.closePopover();
        }
    }
    onClick(target) {
        if (!this.isBrowser)
            return;
        const clickedInside = this.elementRef.nativeElement.contains(target);
        const popoverClick = this.appendToId ? document.getElementById(this.appendToId)?.contains(target) : false;
        if (!clickedInside && !popoverClick && this.closeOnClickOutside && this.menuActive) {
            this.closePopover();
        }
    }
    ngAfterViewInit() {
        this.positionPopover();
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
        this.autoUpdateRef?.();
    }
    openPopover() {
        this.menuActive = false;
        this.positionPopover();
        setTimeout(() => {
            this.menuActive = true;
            this.popoverCurrent.emit(this.id);
        });
    }
    closePopover() {
        this.menuActive = false;
        this.popoverClosed.emit();
    }
    /**
     * If the popover lives on top of a clickable element/parent, pass in the click event - this method will stop event propagation so that ONLY the popover is triggered
     *
     * @param event the click event to trigger the popover
     * Event is optional because it is sometimes inconvenient to pass the click event to this method, and sometimes it isn't needed
     */
    togglePopover(event) {
        setTimeout(() => {
            // if (this.openOnClick) {
            this.menuActive ? this.closePopover() : this.openPopover();
            // }
        });
        event?.stopPropagation();
    }
    positionPopover() {
        if (!this.isBrowser)
            return;
        this.popover = document.getElementById(this.id);
        this.target = document.getElementById(this.targetElement);
        this.popoverRect = this.popover?.getBoundingClientRect();
        this.targetRect = this.target?.getBoundingClientRect();
        if (!this.popover || !this.target) {
            return;
        }
        if (this.repositionDynamically && this.autoUpdateRef === undefined)
            this.repositionOnChanges();
        this.updatePosition();
    }
    computePopoverWidth() {
        if (!this.isBrowser)
            return;
        if (window.innerWidth <= this.mobileWidth) {
            const minWidth = `${window.innerWidth - this.margins}px`;
            const maxWidth = `${window.innerWidth - this.margins}px`;
            return { minWidth, maxWidth };
        }
        return { minWidth: '280px', maxWidth: this.maxWidth };
    }
    repositionOnChanges() {
        this.autoUpdateRef = autoUpdate(this.target, this.popover, this.updatePosition);
    }
    transformPositionForFloatingUi(position) {
        /**
         * For some reason, floating-ui seems to have the right and left positions reversed
         * This is a temporary fix until we can figure out why
         * PS: Seems to work fine in storybook - only breaks in the app
         */
        switch (position) {
            case 'bottom-right':
                return 'bottom-start';
            case 'bottom-left':
                return 'bottom-end';
            case 'top-right':
                return 'top-start';
            case 'top-left':
                return 'top-end';
            default:
                return position;
        }
    }
    setPopoverStyle(style) {
        this.popover.style.top = `${style.top}px`;
        this.popover.style.left = `${style.left}px`;
        this.popover.style['min-width'] = style.minWidth;
        this.popover.style['max-width'] = style.maxWidth;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11PopoverComponent, selector: "e11-popover", inputs: { id: "id", position: "position", targetElement: "targetElement", appendToId: "appendToId", mobileWidth: "mobileWidth", margins: "margins", hasCloser: "hasCloser", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside", closeOnSelect: "closeOnSelect", repositionDynamically: "repositionDynamically", darkMode: "darkMode", hasPadding: "hasPadding", maxWidth: "maxWidth", containerClassOverrides: "containerClassOverrides", contentClassOverrides: "contentClassOverrides", popoverCurrentlyOpen: "popoverCurrentlyOpen" }, outputs: { clickOptionEvent: "clickOptionEvent", popoverCurrent: "popoverCurrent", popoverClosed: "popoverClosed" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)", "document:click": "onClick($event.target)" } }, providers: [], viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true }], ngImport: i0, template: "<div id=\"e11-popover-default-append\"></div>\n<div\n  id=\"{{ id }}\"\n  class=\"e11-popover-container e11-rounded e11-shadow-md e11-absolute e11-border e11-border-skin-grey/50 e11-w-max {{ containerClassOverrides }} \"\n  [ngClass]=\"{\n    open: menuActive,\n    'e11-p-4': hasPadding,\n    'e11-bg-skin-app-bg-dark e11-text-skin-light': darkMode,\n    'e11-bg-skin-white e11-text-black-500': !darkMode\n  }\"\n  [e11AppendTo]=\"appendToId ?? 'e11-popover-default-append'\"\n>\n  <!-- Closer -->\n  <span\n    *ngIf=\"hasCloser\"\n    class=\"e11-popover-close e11-absolute e11-right-5 e11-top-3 e11-flex e11-items-center e11-justify-center e11-rounded-full e11-h-6 e11-w-6 e11-p-4 e11-cursor-pointer hover:e11-bg-skin-grey/20 hover:e11-rounded-full\"\n    (click)=\"closePopover()\"\n  >\n    <span class=\"material-icons\" [ngClass]=\"{ 'e11-text-white/70 hover:e11-text-white': darkMode }\">close</span>\n  </span>\n\n  <!-- Wrapper -->\n  <div class=\"e11-max-h-[40vh] e11-overflow-y-auto {{ contentClassOverrides }}\" [ngClass]=\"{ 'e11-pr-8': hasCloser }\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [".e11-popover-container{min-width:280px;transition:opacity .15s ease-in-out 0s,z-index .25s ease 0s;opacity:0;z-index:-1}.e11-popover-container.open{transition:opacity .15s ease-in-out 0s,z-index .25s ease 0s;opacity:1;z-index:10}.e11-popover-container .e11-popover-close{z-index:10}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.E11AppendToDirective, selector: "[e11AppendTo]", inputs: ["e11AppendTo"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-popover', providers: [], template: "<div id=\"e11-popover-default-append\"></div>\n<div\n  id=\"{{ id }}\"\n  class=\"e11-popover-container e11-rounded e11-shadow-md e11-absolute e11-border e11-border-skin-grey/50 e11-w-max {{ containerClassOverrides }} \"\n  [ngClass]=\"{\n    open: menuActive,\n    'e11-p-4': hasPadding,\n    'e11-bg-skin-app-bg-dark e11-text-skin-light': darkMode,\n    'e11-bg-skin-white e11-text-black-500': !darkMode\n  }\"\n  [e11AppendTo]=\"appendToId ?? 'e11-popover-default-append'\"\n>\n  <!-- Closer -->\n  <span\n    *ngIf=\"hasCloser\"\n    class=\"e11-popover-close e11-absolute e11-right-5 e11-top-3 e11-flex e11-items-center e11-justify-center e11-rounded-full e11-h-6 e11-w-6 e11-p-4 e11-cursor-pointer hover:e11-bg-skin-grey/20 hover:e11-rounded-full\"\n    (click)=\"closePopover()\"\n  >\n    <span class=\"material-icons\" [ngClass]=\"{ 'e11-text-white/70 hover:e11-text-white': darkMode }\">close</span>\n  </span>\n\n  <!-- Wrapper -->\n  <div class=\"e11-max-h-[40vh] e11-overflow-y-auto {{ contentClassOverrides }}\" [ngClass]=\"{ 'e11-pr-8': hasCloser }\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [".e11-popover-container{min-width:280px;transition:opacity .15s ease-in-out 0s,z-index .25s ease 0s;opacity:0;z-index:-1}.e11-popover-container.open{transition:opacity .15s ease-in-out 0s,z-index .25s ease 0s;opacity:1;z-index:10}.e11-popover-container .e11-popover-close{z-index:10}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], position: [{
                type: Input
            }], targetElement: [{
                type: Input
            }], appendToId: [{
                type: Input
            }], mobileWidth: [{
                type: Input
            }], margins: [{
                type: Input
            }], hasCloser: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], closeOnSelect: [{
                type: Input
            }], repositionDynamically: [{
                type: Input
            }], darkMode: [{
                type: Input
            }], hasPadding: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], contentClassOverrides: [{
                type: Input
            }], clickOptionEvent: [{
                type: Output
            }], popoverCurrent: [{
                type: Output
            }], popoverClosed: [{
                type: Output
            }], container: [{
                type: ViewChild,
                args: ['container', { static: false }]
            }], popoverCurrentlyOpen: [{
                type: Input
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class E11PopoverGlobalService {
    constructor() {
        this.config$ = new BehaviorSubject(null);
        this.popoverOpened$ = new Subject();
        this.popoverClosed$ = new Subject();
    }
    togglePopover(config) {
        if (this.currentPopover && this.currentPopover === config.id) {
            this.closePopover();
            return;
        }
        this.openPopover(config);
    }
    openPopover(config) {
        if (this.currentPopover) {
            this.closePopover();
            setTimeout(() => this.openPopover(config));
            return;
        }
        const configToUse = { ...defaultPopoverConfig, ...config };
        this.config$.next(configToUse);
        this.currentPopover = configToUse.id;
        this.popoverOpened$.next(configToUse.id);
    }
    closePopover() {
        this.popoverClosed$.next(this.currentPopover);
        this.currentPopover = undefined;
        this.config$.next(null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverGlobalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverGlobalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverGlobalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Benefits of global popover:
 * - It only requires one popover in the DOM - no filling the DOM with unnecessary popover code
 * - It ensures only ONE popover is open at a time
 */
class E11PopoverGlobalComponent {
    constructor(popoverGlobalService) {
        this.popoverGlobalService = popoverGlobalService;
        this.config = null;
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.popoverGlobalService.config$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
            this.config = config;
            if (config)
                setTimeout(() => this.globalPopover.openPopover());
            else
                setTimeout(() => this.globalPopover?.closePopover());
        });
    }
    popoverClosed() {
        this.popoverGlobalService.closePopover();
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverGlobalComponent, deps: [{ token: E11PopoverGlobalService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11PopoverGlobalComponent, selector: "e11-popover-global", viewQueries: [{ propertyName: "globalPopover", first: true, predicate: ["globalPopover"], descendants: true }], ngImport: i0, template: `
    <e11-popover
      #globalPopover
      *ngIf="config"
      [id]="config.id"
      [position]="config.position"
      [targetElement]="config.targetElement"
      [mobileWidth]="config.mobileWidth"
      [margins]="config.margins"
      [hasCloser]="config.hasCloser"
      [closeOnEscape]="config.closeOnEscape"
      [closeOnClickOutside]="config.closeOnClickOutside"
      [repositionDynamically]="config.repositionDynamically"
      [darkMode]="config.darkMode"
      [hasPadding]="config.hasPadding"
      [maxWidth]="config.maxWidth"
      [containerClassOverrides]="config.containerClassOverrides"
      [contentClassOverrides]="config.contentClassOverrides"
      [appendToId]="config.appendToId"
      (popoverClosed)="popoverClosed()"
    >
      <ng-container *ngTemplateOutlet="config.content"></ng-container>
    </e11-popover>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i3.ClickOutsideDirective, selector: "[closeOnClickOutside]", outputs: ["closeOnClickOutside"] }, { kind: "directive", type: i3.EscapeKeyDirective, selector: "[closeOnEscape]", outputs: ["closeOnEscape"] }, { kind: "component", type: E11PopoverComponent, selector: "e11-popover", inputs: ["id", "position", "targetElement", "appendToId", "mobileWidth", "margins", "hasCloser", "closeOnEscape", "closeOnClickOutside", "closeOnSelect", "repositionDynamically", "darkMode", "hasPadding", "maxWidth", "containerClassOverrides", "contentClassOverrides", "popoverCurrentlyOpen"], outputs: ["clickOptionEvent", "popoverCurrent", "popoverClosed"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverGlobalComponent, decorators: [{
            type: Component,
            args: [{
                    template: `
    <e11-popover
      #globalPopover
      *ngIf="config"
      [id]="config.id"
      [position]="config.position"
      [targetElement]="config.targetElement"
      [mobileWidth]="config.mobileWidth"
      [margins]="config.margins"
      [hasCloser]="config.hasCloser"
      [closeOnEscape]="config.closeOnEscape"
      [closeOnClickOutside]="config.closeOnClickOutside"
      [repositionDynamically]="config.repositionDynamically"
      [darkMode]="config.darkMode"
      [hasPadding]="config.hasPadding"
      [maxWidth]="config.maxWidth"
      [containerClassOverrides]="config.containerClassOverrides"
      [contentClassOverrides]="config.contentClassOverrides"
      [appendToId]="config.appendToId"
      (popoverClosed)="popoverClosed()"
    >
      <ng-container *ngTemplateOutlet="config.content"></ng-container>
    </e11-popover>
  `,
                    selector: 'e11-popover-global',
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [{ type: E11PopoverGlobalService }], propDecorators: { globalPopover: [{
                type: ViewChild,
                args: ['globalPopover']
            }] } });

class E11PopoverModule {
    static forRoot() {
        return {
            ngModule: E11PopoverModule,
            providers: [], // Be extremely careful about declaring services in providers arrays - it can prevent them from being singletons
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverModule, declarations: [E11PopoverComponent, E11PopoverGlobalComponent], imports: [CommonModule, E11DirectivesModule, E11UtilModule], exports: [E11PopoverComponent, E11PopoverGlobalComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverModule, imports: [CommonModule, E11DirectivesModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11PopoverComponent, E11PopoverGlobalComponent],
                    imports: [CommonModule, E11DirectivesModule, E11UtilModule],
                    exports: [E11PopoverComponent, E11PopoverGlobalComponent],
                    providers: [],
                }]
        }] });

class E11PopoverService {
    constructor() {
        this.popoverOpenIdSource = new BehaviorSubject('');
        this.popoverOpenIdObservable$ = this.popoverOpenIdSource.asObservable();
    }
    popoverOpen(popoverId) {
        this.popoverOpenIdSource.next(popoverId);
    }
    getCurrentPopoverId() {
        return this.popoverOpenIdSource.value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PopoverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11PopoverComponent, E11PopoverGlobalComponent, E11PopoverGlobalService, E11PopoverModule, E11PopoverService, defaultPopoverConfig };
